<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import IconComponent from '../common/IconComponent.vue'
import { useNotificationsStore } from '../../../stores/notifications'
import type { PosNotification } from '../../../stores/notifications'

/**
 * Sticky notification bubbles, bottom-right of the viewport.
 *
 * Every fresh unread row picked up by the notifications store's polling
 * loop lands here as a persistent bubble — no auto-dismiss timer. The
 * cashier either:
 *   • clicks the bubble body  → marks the row read + opens the linked
 *                                record (sales-order modal via the same
 *                                deep-link convention the drawer uses, or
 *                                a normal router push for everything else)
 *                                + removes the bubble from the stack.
 *   • clicks the X button     → removes the bubble without touching read state.
 *
 * Teleported to <body> so its visual position is independent of where the
 * component is declared in the tree (App.vue mounts it inside the
 * authenticated branch — once removed from the DOM on logout the queue
 * empties via the store's reset()).
 */

const store = useNotificationsStore()
const router = useRouter()

// Newest at top — `poll()` unshifts onto the queue, so the natural order
// already puts the most recent at index 0. We just expose it.
const bubbles = computed<PosNotification[]>(() => store.bubbleQueue)

/**
 * Severity → vivid colour kit for the WHOLE bubble (not just the side
 * border). The previous white-card-with-coloured-border design was easy to
 * miss against the POS shell's own white cards; this version saturates the
 * entire bubble surface, lays a glowing severity-tinted shadow underneath,
 * and rings the perimeter for extra punch so the bubble stands out
 * regardless of what's behind it. Light + dark mode share the same fill —
 * the gradients carry their own contrast.
 */
function severityClasses(severity: string) {
  switch (severity) {
    case 'urgent':
    case 'error':
      return {
        // Full-bleed crimson gradient — the loudest of the four.
        bubble: 'bg-gradient-to-br from-red-600 to-red-700 ring-2 ring-red-300/70 shadow-2xl shadow-red-500/60',
        iconWrap: 'bg-white/25 ring-1 ring-white/40',
        ribbon: 'bg-white/20 text-white',
        icon: 'AlertTriangle',
      }
    case 'warning':
      return {
        // Warm amber→orange — high-contrast against both light and dark.
        bubble: 'bg-gradient-to-br from-amber-500 to-orange-600 ring-2 ring-amber-300/70 shadow-2xl shadow-amber-500/50',
        iconWrap: 'bg-white/25 ring-1 ring-white/40',
        ribbon: 'bg-white/20 text-white',
        icon: 'AlertCircle',
      }
    case 'success':
      return {
        bubble: 'bg-gradient-to-br from-emerald-500 to-emerald-700 ring-2 ring-emerald-300/70 shadow-2xl shadow-emerald-500/50',
        iconWrap: 'bg-white/25 ring-1 ring-white/40',
        ribbon: 'bg-white/20 text-white',
        icon: 'CheckCircle2',
      }
    default:
      // Info — vivid blue. The default for storefront quotation / order
      // notifications, so chosen to be unmistakable against the white POS
      // chrome the cashier stares at all day.
      return {
        bubble: 'bg-gradient-to-br from-blue-600 to-indigo-700 ring-2 ring-blue-300/70 shadow-2xl shadow-blue-500/60',
        iconWrap: 'bg-white/25 ring-1 ring-white/40',
        ribbon: 'bg-white/20 text-white',
        icon: 'BellRing',
      }
  }
}

function timeAgo(iso: string | null): string {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const diff = Math.max(0, Date.now() - t)
  const s = Math.floor(diff / 1000)
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

async function openBubble(n: PosNotification) {
  // Always mark read on click so the bell badge + drawer + tab stay in sync.
  if (!n.read_at) {
    try { await store.markRead(n.id) } catch { /* tolerated */ }
  }
  store.dismissBubble(n.id)

  if (!n.link) return

  // Same deep-link convention the drawer + OrderSummary tab + full-page
  // tab use: `sales-order:UID` opens the order in a modal on the POS
  // Screen via the pub/sub pendingSalesOrderUid ref. Anything else is a
  // plain router push.
  if (n.link.startsWith('sales-order:')) {
    const uid = n.link.slice('sales-order:'.length)
    store.openSalesOrder(uid)
    if (!router.currentRoute.value.path.startsWith('/pos')) {
      router.push('/pos').catch(() => {})
    }
    return
  }
  router.push(n.link).catch(() => {})
}

function dismissBubble(n: PosNotification, e: Event) {
  e.stopPropagation()
  store.dismissBubble(n.id)
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="bubbles.length > 0"
      class="fixed bottom-4 right-4 z-[200] flex flex-col gap-3 max-w-sm w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto pointer-events-none"
      aria-live="polite"
      aria-label="Notification bubbles"
    >
      <TransitionGroup name="bubble">
        <div
          v-for="n in bubbles"
          :key="n.id"
          role="button"
          tabindex="0"
          @click="openBubble(n)"
          @keydown.enter.prevent="openBubble(n)"
          @keydown.space.prevent="openBubble(n)"
          :class="[
            'pointer-events-auto cursor-pointer rounded-xl text-white hover:scale-[1.02] active:scale-[0.98] transition transform group attention',
            severityClasses(n.severity).bubble,
          ]"
        >
          <div class="p-4 flex gap-3">
            <div
              :class="[
                'w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white',
                severityClasses(n.severity).iconWrap,
              ]"
            >
              <IconComponent :name="severityClasses(n.severity).icon" :size="20" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-baseline gap-2 mb-0.5">
                <h4 class="text-sm font-bold text-white drop-shadow-sm truncate flex-1">
                  {{ n.title }}
                </h4>
                <span class="text-[10px] text-white/75 whitespace-nowrap">
                  {{ timeAgo(n.created_at) }}
                </span>
              </div>
              <p
                v-if="n.body"
                class="text-xs text-white/90 line-clamp-2 leading-snug"
              >
                {{ n.body }}
              </p>
              <div v-if="n.category" class="mt-1.5">
                <span
                  :class="[
                    'inline-block text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-bold',
                    severityClasses(n.severity).ribbon,
                  ]"
                >
                  {{ n.category }}
                </span>
              </div>
            </div>
            <button
              type="button"
              class="text-white/70 hover:text-white hover:bg-white/15 rounded-full p-1 -mt-1 -mr-1 flex-shrink-0 transition"
              :aria-label="`Dismiss notification: ${n.title}`"
              @click="dismissBubble(n, $event)"
            >
              <IconComponent name="X" :size="16" />
            </button>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.bubble-enter-active {
  transition: all 0.35s cubic-bezier(0.21, 1.02, 0.73, 1);
}
.bubble-leave-active {
  transition: all 0.25s cubic-bezier(0.06, 0.71, 0.55, 1);
  position: absolute;
  right: 0;
  left: 0;
}
.bubble-enter-from {
  opacity: 0;
  transform: translateX(60px) scale(0.92);
}
.bubble-leave-to {
  opacity: 0;
  transform: translateX(60px) scale(0.92);
}
.bubble-move {
  transition: transform 0.3s ease;
}

/* Brief attention-grabbing breathe on entry. Runs twice, then settles — the
 * cashier sees the bubble "land" with a heartbeat before it goes static, so
 * a new notification is hard to miss even out of the corner of an eye. The
 * box-shadow animation also makes the severity glow visibly pulse. */
@keyframes bubble-attention {
  0%, 100% { transform: scale(1); filter: brightness(1); }
  50% { transform: scale(1.03); filter: brightness(1.12); }
}
.attention {
  animation: bubble-attention 1.2s ease-in-out 2;
}
</style>
